<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">{{ $t("News Feed") }}</h5>
          <small class="text-muted">{{
            $t("Manage school news and announcements")
          }}</small>
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }}
            <strong>{{ displayedRange.end }}</strong> {{ $t("of") }}
            <strong>{{ filteredTotalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredTotalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="onPageChange"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group filter-fields-group--search-only d-flex align-items-center"
          >
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input"
              :placeholder="$t('Search') + '...'"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>{{ $t("Add News Feed") }}</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container newsfeed-table-scroll">
        <div v-if="dataLoading" class="newsfeed-skeleton-wrap">
          <table class="table newsfeed-skeleton-table mb-0">
            <thead>
              <tr>
                <th
                  v-for="field in fields"
                  :key="'newsfeed-sk-head-' + field.key"
                  :class="field.thClass"
                >
                  <div class="skeleton-loader skeleton-loader--sm"></div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="n in 6"
                :key="'newsfeed-skeleton-' + n"
                class="skeleton-row"
              >
                <td
                  v-for="(field, index) in fields"
                  :key="'newsfeed-skeleton-cell-' + n + '-' + index"
                  :class="[field.tdClass, 'skeleton-cell']"
                >
                  <div
                    v-if="field.key === 'actions'"
                    class="newsfeed-skeleton-actions"
                  >
                    <div class="skeleton-loader skeleton-loader--action"></div>
                    <div class="skeleton-loader skeleton-loader--action"></div>
                    <div class="skeleton-loader skeleton-loader--action"></div>
                  </div>
                  <div
                    v-else
                    class="skeleton-loader"
                    :class="{
                      'mx-auto': field.tdClass && field.tdClass.includes('text-center'),
                    }"
                    :style="{ width: getNewsfeedSkeletonWidth(index) }"
                  ></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <b-table
          v-else
          class="planner-list-table mt-1"
          :items="paginatedNewsFeeds"
          :fields="fields"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 class="mt-1 mb-1 text-center">{{ $t("No records found") }}</h3>
          </template>

          <template #head()="scope">
            <div>{{ $t(scope.label) }}</div>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(caption)="{ item }">
            <span
              class="table-caption-text"
              :title="getCaptionPreview(item.caption, 220)"
              @click="viewNewsFeed(item)"
            >
              {{ getCaptionPreview(item.caption) }}
            </span>
          </template>

          <template #cell(media)="{ item }">
            <b-badge v-if="item.media" variant="light-success">
              <feather-icon icon="ImageIcon" size="14" class="mr-50" />
              {{ $t("Media Attached") }}
            </b-badge>
            <b-badge v-else variant="light-secondary">
              {{ $t("No Media") }}
            </b-badge>
          </template>

          <template #cell(name)="{ item }">
            <b-badge variant="light-primary">{{ item.name }}</b-badge>
          </template>

          <template #cell(designation)="{ item }">
            <b-badge variant="light-info">{{ item.designation }}</b-badge>
          </template>

          <template #cell(date)="{ item }">
            <b-badge variant="light-warning">{{
              formatDate(item.date)
            }}</b-badge>
          </template>

          <template #head(actions)>
            <div class="text-center">
              <span>{{ $t("Actions") }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center flex-nowrap">
              <b-button
                variant="outline-primary"
                v-b-tooltip.hover.top
                :title="$t('View')"
                class="action-btn-outline mr-1"
                @click="viewNewsFeed(item)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
              <b-button
                variant="gradient-primary"
                v-b-tooltip.hover.top
                :title="$t('Edit')"
                class="action-btn-table mr-1"
                @click="editNewsFeed(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                variant="outline-danger"
                v-b-tooltip.hover.top
                :title="$t('Delete')"
                class="action-btn-outline delete-btn"
                @click="deleteNewsFeed(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? $t('Edit News Feed') : $t('Add News Feed')"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
      size="lg"
      body-class="newsfeed-form-modal-body"
      content-class="newsfeed-form-modal"
      scrollable
    >
      <b-form @submit.prevent class="newsfeed-form-scroll">
        <b-row>
          <b-col md="12">
            <b-form-group :label="$t('Caption') + ' *'" label-for="caption">
              <quill-editor
                id="newsfeed-caption"
                :key="`newsfeed-caption-${form.id || 'new'}-${showModal}`"
                v-model="form.caption"
                :options="{
                  modules: {
                    toolbar: editorOptions,
                  },
                  placeholder: $t('Enter news feed caption'),
                  theme: 'snow',
                }"
                :disabled="loading"
                class="newsfeed-quill-editor"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-col md="12">
          <div class="d-flex justify-content-between">
            <label class="bv-no-focus-ring col-form-label pt-0">{{
              $t("Media Attachments")
            }}</label>
          </div>
          <div class="d-flex flex-wrap modern-attachment-zone">
            <div
              class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1"
              style="width: 80px; height: 80px"
              @click.stop="!mediaLoading && $refs.mediaFiles.click()"
            >
              <b-spinner
                v-if="mediaLoading"
                small
                label="Loading..."
                variant="primary"
              />
              <feather-icon
                v-else
                icon="PlusIcon"
                size="34"
                class="text-primary"
              />
            </div>
            <div
              class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1"
              style="width: 80px; height: 80px; position: relative"
              v-for="(file, index) in mediaList"
              :key="index"
              @click="openFile(file)"
            >
              <b-avatar
                size="20"
                variant="primary"
                style="position: absolute; top: -5px; right: -5px"
                button
                @click.stop="removeMedia(index)"
              >
                <feather-icon icon="XIcon" />
              </b-avatar>
              <template v-if="isImageMedia(file)">
                <img
                  :src="getMediaPreview(file)"
                  class="w-100 h-100 rounded newsfeed-media-preview"
                  referrerpolicy="no-referrer"
                  :alt="$t('Media preview')"
                  @error="onMediaPreviewError($event, file)"
                />
              </template>
              <template v-else-if="isVideoMedia(file)">
                <feather-icon
                  icon="VideoIcon"
                  size="30"
                  class="text-primary"
                />
              </template>
              <template v-else>
                <feather-icon
                  icon="FileTextIcon"
                  size="30"
                  class="text-primary"
                />
              </template>
            </div>

            <input
              type="file"
              id="mediaFiles"
              hidden
              ref="mediaFiles"
              multiple
              accept="image/*,video/*,application/pdf"
              @change="uploadMedia"
            />
          </div>
        </b-col>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          {{ $t("Cancel") }}
        </b-button>
        <b-button variant="primary" :disabled="loading" @click="saveNewsFeed">
          <b-spinner v-if="loading" small />
          {{ isEdit ? $t("Update") : $t("Save") }}
        </b-button>
      </template>
    </b-modal>

    <!-- View Modal -->
    <b-modal
      v-model="showViewModal"
      hide-header
      @hidden="selectedItem = null"
      centered
      no-close-on-backdrop
      size="lg"
      body-class="p-0 newsfeed-view-modal-body"
      content-class="lr-modal-content circular-view-modal newsfeed-view-modal"
      scrollable
    >
      <div class="lr-modal-header">
        <div class="lr-modal-header-left">
          <div class="lr-modal-icon-wrap">
            <feather-icon icon="FileTextIcon" size="20" />
          </div>
          <div>
            <h5 class="lr-modal-title mb-0">{{ $t("News Feed Details") }}</h5>
            <small class="lr-modal-subtitle">{{
              $t("View announcement content")
            }}</small>
          </div>
        </div>
        <b-button
          variant="flat-secondary"
          class="lr-modal-close-btn"
          @click="showViewModal = false"
        >
          <feather-icon icon="XIcon" size="18" />
        </b-button>
      </div>

      <div v-if="selectedItem" class="lr-modal-body newsfeed-view-scroll">
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="InfoIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Post Information") }}</h6>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Posted By") }}</span>
              <span class="details-value details-value-primary">{{
                selectedItem.name || "—"
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Campus") }}</span>
              <span class="details-value details-value-info">{{
                selectedItem.designation || "—"
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Posted Date") }}</span>
              <span class="details-value details-value-primary">{{
                formatDate(selectedItem.date)
              }}</span>
            </div>
          </div>
        </div>

        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="AlignLeftIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Caption") }}</h6>
          </div>
          <div
            class="newsfeed-caption-html"
            dir="auto"
            v-html="selectedItem.caption"
          ></div>
        </div>

        <div v-if="viewMediaList.length" class="leave-details-section mb-0">
          <div class="section-header">
            <feather-icon icon="ImageIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Media Attachments") }}</h6>
          </div>
          <div class="newsfeed-view-media-grid">
            <div
              v-for="(file, index) in viewMediaList"
              :key="`view-media-${index}`"
              class="newsfeed-view-media-item"
              @click="openFile(file)"
            >
              <template v-if="isImageMedia(file)">
                <img
                  :src="getMediaPreview(file)"
                  class="newsfeed-view-media-img"
                  referrerpolicy="no-referrer"
                  :alt="$t('Media preview')"
                  @error="onMediaPreviewError($event, file)"
                />
              </template>
              <template v-else-if="isVideoMedia(file)">
                <feather-icon icon="VideoIcon" size="28" class="text-primary" />
              </template>
              <template v-else>
                <feather-icon
                  icon="FileTextIcon"
                  size="28"
                  class="text-primary"
                />
              </template>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showViewModal = false">
          {{ $t("Close") }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  BBadge,
  BAvatar,
  BImg,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { quillEditor } from "vue-quill-editor";
import { mapActions } from "vuex";
import {
  getUploadedPaths,
  uploadToMySkool,
  buildUploadedAttachment,
  normalizeDocAttachment,
  isImageUrl,
  getMediaPreviewUrl,
  handleDrivePreviewError,
  getDocUrl,
} from "@/utils/gdriveUpload";

export default {
  name: "NewsFeed",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    BBadge,
    BAvatar,
    BImg,
    vSelect,
    quillEditor,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      newsFeeds: [],
      searchQuery: "",
      showModal: false,
      showViewModal: false,
      selectedItem: null,
      isEdit: false,
      loading: false,
      dataLoading: false,
      currentPage: 1,
      perPage: 20,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      totalCount: 0,
      fields: [
        {
          label: "S.No",
          key: "index",
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          key: "caption",
          label: "Caption",
          thClass: "col-caption",
          tdClass: "col-caption",
        },
        { key: "media", label: "Media" },
        { key: "name", label: "posted by" },
        { key: "designation", label: "Campus" },
        { key: "date", label: "posted date" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      mediaLoading: false,
      mediaList: [],
      editorOptions: [
        ["bold", "italic", "underline", "strike"],
        [{ script: "sub" }, { script: "super" }],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link"],
      ],
      form: {
        id: 0,
        caption: "",
        media: "",
        campusID: this.$store.state.userData.cId,
        addedBy: this.$store.state.userData.userID,
        addedOn: null,
        updatedBy: null,
        updatedOn: null,
      },
    };
  },
  computed: {
    filteredNewsFeeds() {
      if (!this.searchQuery) return this.newsFeeds;

      const search = this.searchQuery.toLowerCase();
      return this.newsFeeds.filter(
        (feed) =>
          this.stripHtmlTags(feed.caption).toLowerCase().includes(search) ||
          feed.name?.toLowerCase().includes(search) ||
          feed.designation?.toLowerCase().includes(search)
      );
    },
    filteredTotalCount() {
      return this.filteredNewsFeeds.length;
    },
    paginatedNewsFeeds() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredNewsFeeds.slice(start, start + this.perPage);
    },
    displayedRange() {
      if (!this.filteredTotalCount) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(
        this.currentPage * this.perPage,
        this.filteredTotalCount
      );
      return { start, end };
    },
    viewMediaList() {
      if (!this.selectedItem?.media) return [];
      return this.selectedItem.media
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean)
        .map((url) => normalizeDocAttachment(url));
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
  created() {
    this.loadNewsFeeds();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    onPageChange() {
      // client-side pagination only
    },

    onPageSizeChange() {
      this.currentPage = 1;
    },

    getNewsfeedSkeletonWidth(columnIndex) {
      const widths = ["35%", "85%", "55%", "60%", "50%", "55%", "65%"];
      return widths[columnIndex] || "60%";
    },

    async loadNewsFeeds() {
      this.dataLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "NewsFeed/GetFeed?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = await this.get(obj);

        if (res && res.status === "success" && Array.isArray(res.data)) {
          this.newsFeeds = res.data;
          this.totalCount = res.data.length;
        } else if (res && Array.isArray(res)) {
          this.newsFeeds = res;
          this.totalCount = res.length;
        } else {
          this.newsFeeds = [];
          this.totalCount = 0;
        }
      } catch (error) {
        console.error("Error loading news feeds:", error);
        this.$bvToast.toast(this.$t("Error loading news feeds!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        this.newsFeeds = [];
        this.totalCount = 0;
      }
      this.dataLoading = false;
    },

    openAddModal() {
      this.isEdit = false;
      this.showModal = true;
    },

    viewNewsFeed(item) {
      this.selectedItem = { ...item };
      this.showViewModal = true;
    },

    editNewsFeed(item) {
      this.isEdit = true;
      const normalizedMedia = this.normalizeMediaForForm(item.media);
      const normalizedDate =
        item.date || item.postedDate || item.addedOn || item.postedOn || null;
      this.form = {
        ...item,
        caption: this.normalizeCaptionForEditor(item.caption),
        media: normalizedMedia,
        date: normalizedDate,
      };
      this.mediaList = normalizedMedia
        ? normalizedMedia
            .split(",")
            .map((url) => url.trim())
            .filter(Boolean)
            .map((url) => normalizeDocAttachment(url))
        : [];
      this.showModal = true;
    },

    async deleteNewsFeed(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: this.$t("Yes, delete it!"),
      });

      if (result.isConfirmed) {
        try {
          const obj = {
            url:
              this.$store.state.domain +
              "NewsFeed/" +
              item.id +
              "?db=" +
              this.$store.state.userData.db,
            message: "News feed deleted successfully.",
            context: this,
            token: this.$store.state.userData.token,
          };
          const success = await this.delete(obj);
          if (success) {
            this.loadNewsFeeds();
          }
        } catch (error) {
          console.error("Error deleting news feed:", error);
          this.$bvToast.toast(this.$t("Error deleting news feed!"), {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          });
        }
      }
    },

    async saveNewsFeed() {
      if (!this.getCaptionTextContent(this.form.caption)) {
        this.$bvToast.toast(this.$t("Please fill the caption field!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;

      try {
        if (this.isEdit) {
          this.form.updatedBy = this.$store.state.userData.userID;
          this.form.updatedOn = new Date().toISOString();
        } else {
          this.form.addedOn = new Date().toISOString();
        }

        let obj;
        if (this.isEdit) {
          obj = {
            url:
              this.$store.state.domain +
              "NewsFeed/" +
              this.form.id +
              "?db=" +
              this.$store.state.userData.db,
            body: {
              id: this.form.id,
              caption: this.normalizeCaptionForSave(this.form.caption),
              media: this.form.media || "",
              campusID: this.form.campusID,
              date: this.form.date || null,
            },
            message: "News feed updated successfully.",
            context: this,
            token: this.$store.state.userData.token,
          };
          const success = await this.put(obj);
          if (success) {
            this.showModal = false;
            this.loadNewsFeeds();
          }
        } else {
          obj = {
            url:
              this.$store.state.domain +
              "NewsFeed?db=" +
              this.$store.state.userData.db,
            body: {
              caption: this.normalizeCaptionForSave(this.form.caption),
              media: this.form.media || "",
              campusID: this.form.campusID,
              addedBy: this.form.addedBy,
            },
            message: "News feed added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          };
          const success = await this.post(obj);
          if (success) {
            this.showModal = false;
            this.loadNewsFeeds();
          }
        }
      } catch (error) {
        console.error("Error saving news feed:", error);
        this.$bvToast.toast(this.$t("Error saving news feed!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
      this.loading = false;
    },

    resetForm() {
      this.form = {
        id: 0,
        caption: "",
        media: "",
        date: null,
        campusID: this.$store.state.userData.cId,
        addedBy: this.$store.state.userData.userID,
        addedOn: null,
        updatedBy: null,
        updatedOn: null,
      };
      this.mediaList = [];
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString();
    },

    decodeHtmlEntities(text) {
      if (!text) return "";
      if (typeof document === "undefined") return String(text);
      const textarea = document.createElement("textarea");
      textarea.innerHTML = String(text);
      return textarea.value;
    },

    stripHtmlTags(text) {
      if (!text) return "";

      // API sometimes returns escaped HTML (e.g. &lt;font ...&gt;)
      // Convert entities back to real tags first, then strip tags.
      const decoded = this.decodeHtmlEntities(text);

      return String(decoded)
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<\/(p|div|li|h[1-6])>/gi, " ")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
    },

    getCaptionTextContent(html) {
      return this.stripHtmlTags(html);
    },

    normalizeCaptionForEditor(html) {
      if (!html) return "";
      let normalized = this.decodeHtmlEntities(String(html));

      normalized = normalized.replace(
        /<font\s+color=["']([^"']+)["']\s*>/gi,
        '<span style="color:$1">'
      );
      normalized = normalized.replace(/<\/font>/gi, "</span>");
      normalized = normalized.replace(/<div><br><\/div>/gi, "<p><br></p>");
      normalized = normalized.replace(/<div>/gi, "<p>");
      normalized = normalized.replace(/<\/div>/gi, "</p>");
      return normalized;
    },

    normalizeCaptionForSave(html) {
      if (!html) return "";
      return String(html).trim();
    },

    normalizeMediaForForm(media) {
      if (media === null || media === undefined) return "";
      if (Array.isArray(media)) return media.join(",");
      return String(media).trim();
    },

    getCaptionPreview(text, limit = 120) {
      const plain = this.stripHtmlTags(text);
      if (!plain) return "";
      if (plain.length <= limit) return plain;
      return `${plain.slice(0, limit).trim()}...`;
    },

    isImageMedia(file) {
      const doc = normalizeDocAttachment(file);
      if (isImageUrl(doc.url, doc.type)) return true;
      if (this.isPdfMedia(file) || this.isVideoMedia(file)) return false;
      // GDrive / CDN uploads often have no file extension in the URL
      return /drive\.google\.com|googleusercontent\.com|cloudious\.net|gdrive\./i.test(
        doc.url
      );
    },

    isVideoMedia(file) {
      const doc = normalizeDocAttachment(file);
      const type = String(doc.type || "").toLowerCase();
      if (type === "video") return true;
      return /\.(mp4|webm|mov|amv)(\?|#|$)/i.test(doc.url);
    },

    isPdfMedia(file) {
      const doc = normalizeDocAttachment(file);
      const type = String(doc.type || "").toLowerCase();
      if (type === "pdf") return true;
      return /\.(pdf|doc|ppt|docx|xls|xlsx)(\?|#|$)/i.test(doc.url);
    },

    getMediaPreview(file) {
      const doc = normalizeDocAttachment(file);
      if (doc.previewUrl) return doc.previewUrl;
      return getMediaPreviewUrl(doc.url, "Image");
    },

    onMediaPreviewError(event, file) {
      const doc = normalizeDocAttachment(file);
      handleDrivePreviewError(event, doc.url, "image");
    },

    openFile(url) {
      window.open(getDocUrl(url), "_blank");
    },

    async uploadMedia() {
      if (!this.$refs.mediaFiles?.files?.length) return;

      const files = Array.from(this.$refs.mediaFiles.files);
      this.mediaLoading = true;

      try {
        const uploaded = await uploadToMySkool(files);
        const uploadedPaths = getUploadedPaths(uploaded);
        if (!uploadedPaths.length) {
          throw new Error("Upload failed");
        }

        uploaded.forEach((item) => {
          this.mediaList.push(buildUploadedAttachment(item));
        });
        this.form.media = this.mediaList.map((item) => getDocUrl(item)).join(",");
        this.$bvToast.toast(this.$t("Media uploaded successfully!"), {
          title: "Success!",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        console.error("Upload error:", error);
        this.$bvToast.toast(this.$t("Failed to upload media"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.mediaLoading = false;
        this.$refs.mediaFiles.value = "";
      }
    },

    removeMedia(index) {
      this.mediaList.splice(index, 1);
      this.form.media = this.mediaList.map((item) => getDocUrl(item)).join(",");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";
@import "../schoolinfo/circularsModern.scss";

.cursor-pointer {
  cursor: pointer;
}

.newsfeed-media-preview {
  object-fit: cover;
  display: block;
}

.newsfeed-table-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #c4b5fd #f3f4f6;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c4b5fd;
    border-radius: 8px;
  }
}

.newsfeed-skeleton-wrap {
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 2px 8px rgba(67, 89, 113, 0.06),
    0 8px 24px rgba(115, 103, 240, 0.1);
  border: 1px solid #e4deff;
  background: #fff;
  padding: 0.15rem;
  margin-top: 0.25rem;
}

.newsfeed-skeleton-table {
  min-width: 100%;
  width: max-content;
  border-radius: 14px;
  overflow: hidden;
  border-collapse: separate;
  border-spacing: 0;

  th {
    background: linear-gradient(180deg, #f3f0ff 0%, #ebe6ff 100%);
    border-bottom: 2px solid #ddd4ff;
    padding: 0.95rem 1rem;
    color: #4a4568;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.4px;
    white-space: nowrap;
  }

  th.col-caption {
    min-width: 180px;
    max-width: 320px;
  }

  td {
    padding: 1rem;
    background: #fff;
    vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
  }

  td.col-caption {
    min-width: 180px;
    max-width: 320px;
  }

  tr:nth-child(even) td {
    background: #faf9ff;
  }
}

.skeleton-loader--sm {
  height: 14px;
  width: 70%;
}

.newsfeed-skeleton-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.skeleton-loader--action {
  width: 36px !important;
  height: 36px !important;
  border-radius: 8px;
  flex-shrink: 0;
}

::v-deep .newsfeed-form-modal {
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
}

::v-deep .newsfeed-form-modal-body {
  overflow-y: auto;
  max-height: calc(100vh - 12rem);
  padding: 1rem 1.25rem;
}

::v-deep .newsfeed-view-modal {
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
}

::v-deep .newsfeed-view-modal-body {
  overflow-y: auto;
  max-height: calc(100vh - 2rem);
  padding: 0 !important;
}

.newsfeed-view-scroll {
  max-height: calc(100vh - 8rem);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.newsfeed-form-scroll {
  min-width: 0;
}

::v-deep .planner-list-table {
  th.col-caption,
  td.col-caption {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    vertical-align: top;
    min-width: 180px;
    max-width: 320px;
  }
}

.newsfeed-quill-editor {
  ::v-deep .ql-container {
    min-height: 140px;
    max-height: 280px;
    font-size: 0.95rem;
    overflow-y: auto;
  }

  ::v-deep .ql-editor {
    min-height: 140px;
    max-height: 240px;
    overflow-y: auto;
    line-height: 1.55;
  }

  ::v-deep .ql-toolbar {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    border-color: #e0d8ff;
    background: #faf9ff;
  }

  ::v-deep .ql-container {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    border-color: #e0d8ff;
    background: #fff;
  }
}

.table-caption-text {
  display: block;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.45;
  font-weight: 600;
  color: #2d1b69;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #7367f0;
    text-decoration: underline;
  }
}

.newsfeed-caption-html {
  padding: 1rem 1.1rem;
  border: 1px solid #ebe9f1;
  border-radius: 12px;
  background: #fafafa;
  color: #2d3436;
  line-height: 1.6;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-height: 360px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  ::v-deep p,
  ::v-deep div {
    margin-bottom: 0.5rem;
  }

  ::v-deep p:last-child,
  ::v-deep div:last-child {
    margin-bottom: 0;
  }

  ::v-deep a {
    color: #7367f0;
    text-decoration: underline;
    word-break: break-all;
  }
}

.newsfeed-view-media-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.newsfeed-view-media-item {
  width: 96px;
  height: 96px;
  border: 1px dashed #d8d6de;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(115, 103, 240, 0.15);
  }
}

.newsfeed-view-media-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (max-width: 767.98px) {
  ::v-deep .newsfeed-form-modal-body {
    max-height: calc(100vh - 10rem);
  }

  .newsfeed-view-scroll {
    max-height: calc(100vh - 6rem);
  }

  ::v-deep .planner-list-table {
    min-width: 720px;
  }

  .newsfeed-skeleton-table {
    min-width: 720px;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>
